<template>
  <div v-if="modelValue" class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
    <div class="bg-white w-full max-w-lg rounded-xl shadow-lg p-6">
      <!-- Header -->

      <div class="flex justify-between items-center mb-4">
        <h2 class="text-lg font-semibold text-gray-800">
          {{ job ? 'Edit Job' : 'Post a New Job' }}
        </h2>
        <button @click="close" class="text-gray-400 hover:text-gray-600 text-xl">&times;</button>
      </div>

      <!-- Form -->
      <form @submit.prevent="confirmSubmit" class="space-y-4">
        <div>
          <label class="text-sm text-gray-700">Job Title</label>
          <input v-model="form.title" type="text" placeholder="e.g. Accounting Clerk"
            class="w-full mt-1 border rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-orange-400"
            required />
        </div>

        <div class="grid grid-cols-2 gap-4">
          <div>
            <label class="text-sm text-gray-700">Location</label>
            <input v-model="form.location" type="text" placeholder="e.g. Ermita, Manila / Muntinlupa"
              class="w-full mt-1 border rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-orange-400"
              required />
          </div>
          <div>
            <label class="text-sm text-gray-700">Job Type</label>
            <select v-model="form.type"
              class="w-full mt-1 border rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-orange-400 bg-white"
              required>
              <option value="">Select type</option>
              <option v-for="type in jobTypes" :key="type" :value="type">{{ type }}</option>
            </select>
          </div>
        </div>

        <div>
          <label class="text-sm text-gray-700">Department</label>
          <select v-model="form.department"
            class="w-full mt-1 border rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-orange-400 bg-white"
            required>
            <option value="">Select department</option>
            <option v-for="dept in departments" :key="dept" :value="dept">{{ dept }}</option>
          </select>
        </div>

        <div>
          <label class="text-sm text-gray-700">Description</label>
          <textarea v-model="form.description" rows="3" placeholder="Describe the role and responsibilities..."
            class="w-full mt-1 border rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:border-orange-400"
            required />
        </div>

        <p v-if="formError" class="text-xs text-red-500">{{ formError }}</p>

        <div class="flex justify-end gap-2 pt-2">
          <button type="button" @click="close"
            class="px-4 py-2 text-sm rounded-lg border text-gray-600 hover:bg-gray-100">
            Cancel
          </button>
          <button type="submit" class="px-4 py-2 text-sm rounded-lg bg-orange-500 text-white hover:bg-orange-600">
            {{ job ? 'Update Job' : 'Publish Job' }}
          </button>
        </div>
      </form>
    </div>

    <ConfirmationModal :show="showConfirm" :title="confirmTitle" :message="confirmMessage" :loading="confirmLoading"
      confirmText="Yes" cancelText="Cancel" @confirm="onConfirm" @cancel="showConfirm = false" />
  </div>
</template>

<script>
import { defineComponent, ref, watch } from "vue";
import ConfirmationModal from "@/components/modals/ConfirmationModal.vue";
import { createJob, updateJob } from "@/api/jobs";

const EMPTY_FORM = {
  title: "",
  location: "",
  type: "",
  department: "",
  description: "",
  posted_at: null,
};

export default defineComponent({
  name: "AddJobModal",
  components: { ConfirmationModal },
  props: {
    modelValue: { type: Boolean, required: true },
    job: { type: Object, default: null },
  },
  emits: ["update:modelValue", "job-added", "refresh-jobs"],

  setup(props, { emit }) {
    const form = ref({ ...EMPTY_FORM });
    const formError = ref("");

    const showConfirm = ref(false);
    const confirmLoading = ref(false);
    const confirmTitle = ref("Confirm Job Posting");
    const confirmMessage = ref("");

    const close = () => emit("update:modelValue", false);

    watch(
      () => props.modelValue,
      (val) => {
        if (val) {
          form.value = props.job ? { ...props.job } : { ...EMPTY_FORM };
          formError.value = "";
        }
      }
    );

    const jobTypes = ["Contract", "Full-time", "Internship", "Part-time", "Remote"];
    const departments = [
      "Accounting",
      "Astral Clinic",
      "Client Relations",
      "Corporate Services",
      "Customer Services",
      "Executive Office",
      "Health Provider & Patient Relations",
      "Information & Technology",
      "Muntinlupa Clinic",
    ];

    const confirmSubmit = () => {
      const { title, location, type, department, description } = form.value;
      if (!title || !location || !type || !department || !description) {
        formError.value = "Please fill in all required fields.";
        return;
      }
      confirmMessage.value = `Are you sure you want to ${props.job ? 'update' : 'publish'} the job "${title}"?`;
      showConfirm.value = true;
    };

    const onConfirm = async () => {
      confirmLoading.value = true;
      formError.value = "";
      try {
        let responseData;
        const payload = { ...form.value, posted_at: props.job ? form.value.posted_at : new Date().toISOString() };

        if (props.job) {
          const { data } = await updateJob(props.job.id, payload);
          responseData = data;
        } else {
          const { data } = await createJob(payload);
          responseData = data;
        }

        emit("job-added", { ...responseData, postedDate: new Date(responseData.posted_at).toLocaleDateString() });
        emit("refresh-jobs");
        close();
      } catch (err) {
        console.error("Failed to save job:", err);
        formError.value = err?.response?.data?.message || "Failed to save job.";
      } finally {
        confirmLoading.value = false;
        showConfirm.value = false;
      }
    };

    return {
      form,
      jobTypes,
      formError,
      departments,
      showConfirm,
      confirmTitle,
      confirmMessage,
      confirmLoading,
      close,
      onConfirm,
      confirmSubmit,

    };
  },
});
</script>